import { Button, ButtonProps, Checkbox, Tooltip, TooltipProps } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { MetaData, useTemplateContext } from './TemplateContext';
import { clone, setWith } from 'lodash';
import { colors } from 'theme';
import { LockFilled, UnlockOutlined } from '@ant-design/icons';

const NewTooltip = (props: TooltipProps) => {
    return (
        <StyledTooltip
            color="white"
            overlayInnerStyle={{
                background: colors.white,
                color: colors.grey10,
                fill: colors.white,
            }}
            {...props}
        />
    );
};

const StyledTooltip = styled(Tooltip)`
    .ant-tooltip-arrow-content {
        background-color: ${colors.white} !important;
    }
`;

interface Props {
    fieldPath: string | undefined;
    disabled?: boolean;
    onMakeInvisible?: (name: string | undefined) => void;
    specialName?: string;
    onChange?: (value: any) => void;
    required?: boolean;
    inCheckbox?: boolean;
}

const TemplateCheckboxes = ({
    fieldPath,
    disabled,
    onMakeInvisible,
    specialName,
    onChange,
    required,
    inCheckbox,
}: Props) => {
    const { templateFormik, form } = useTemplateContext();

    const _fieldPath = useMemo(() => {
        // This is to handle fields that have multiple values
        if (fieldPath?.match(/\.[^.]*,.*/g)) return fieldPath?.replace(/\.[^.]*,.*/g, '');
        return fieldPath;
    }, [fieldPath]);

    const removeDefaultValue = useCallback(() => {
        if (onChange) {
            onChange(null);
        } else if (templateFormik?.getFieldProps(`${form}.defaults.${_fieldPath}`).value) {
            templateFormik?.setFieldValue(`${form}.defaults.${_fieldPath}`, null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_fieldPath, form, onChange, templateFormik?.getFieldProps(`${form}.defaults.${_fieldPath}`).value]);

    const metaPath = useMemo(() => `${form}.meta.['${specialName ?? _fieldPath}']`, [_fieldPath, form, specialName]);
    const meta = useMemo(
        () =>
            (templateFormik?.getFieldMeta(metaPath).value as MetaData | undefined) ?? {
                isViewable: true,
                isEditable: true,
            },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [templateFormik?.getFieldMeta(metaPath).value]
    );

    const handleChange = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkboxType = e.target.name as keyof MetaData;
            let _meta: MetaData = meta ? { ...meta } : { isViewable: false, isEditable: false };

            let metaCopy = { ..._meta };

            _meta.isViewable = false;
            _meta.isEditable = false;

            const firstCheckIsEditable = checkboxType === 'isEditable' && !meta;
            const wasChecked = metaCopy[checkboxType];

            if (required || (!firstCheckIsEditable && !wasChecked)) {
                _meta[checkboxType] = !_meta[checkboxType];
            }

            if (_meta.isEditable === meta?.isEditable && _meta.isViewable === meta?.isViewable) return;

            if (!_meta.isEditable && !_meta.isViewable) {
                onMakeInvisible?.(_fieldPath);
                removeDefaultValue();
            }

            const values = templateFormik?.values ?? {};
            templateFormik?.validateForm(setWith(clone(values), metaPath, _meta, clone));
            templateFormik?.setFieldValue(metaPath, { ..._meta });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [_fieldPath, meta, metaPath, onMakeInvisible, removeDefaultValue, required]
    );

    const toggleViewable = useCallback(
        () => {
            let _meta: MetaData = { ...meta };

            if (_meta['isEditable'] === undefined) {
                _meta['isEditable'] = false;
            }

            _meta['isViewable'] = !meta?.isViewable;
            if (!_meta['isViewable'] && meta.isEditable) {
                if (required) {
                    _meta['isViewable'] = true;
                } else {
                    _meta['isEditable'] = false;
                }
            } else if (!_meta['isViewable'] && !meta.isEditable) {
                if (required) {
                    _meta['isViewable'] = true;
                } else {
                    onMakeInvisible?.(_fieldPath);
                    removeDefaultValue();
                }
            }

            const values = templateFormik?.values ?? {};
            templateFormik?.validateForm(setWith(clone(values), metaPath, _meta, clone));
            templateFormik?.setFieldValue(metaPath, { ..._meta });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [_fieldPath, meta, metaPath, onMakeInvisible, removeDefaultValue, required]
    );

    const toggleEditable = useCallback(
        () => {
            let _meta: MetaData = { ...meta };

            if (_meta['isViewable'] === undefined) {
                _meta['isViewable'] = true;
            }

            _meta['isEditable'] = !meta.isEditable;
            if (_meta['isEditable'] && !meta.isViewable) {
                _meta['isViewable'] = true;
            } else if (!_meta['isEditable'] && !meta.isViewable) {
                if (required) {
                    _meta['isViewable'] = true;
                } else {
                    onMakeInvisible?.(_fieldPath);
                    removeDefaultValue();
                }
            }

            const values = templateFormik?.values ?? {};
            templateFormik?.validateForm(setWith(clone(values), metaPath, _meta, clone));
            templateFormik?.setFieldValue(metaPath, { ..._meta });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [_fieldPath, meta, metaPath, onMakeInvisible, removeDefaultValue, required]
    );

    const render = useMemo(
        () => (
            <>
                <LockButton
                    disabled={disabled}
                    editable={meta.isEditable}
                    onToggle={toggleEditable}
                    inCheckbox={inCheckbox}
                />
                <NewTooltip title={`User will ${meta?.isViewable ? '' : 'not '}be able to view the default value`}>
                    <TemplateCheckbox
                        disabled={disabled}
                        name="isViewable"
                        onChange={toggleViewable}
                        checked={meta.isViewable}
                        size="small"
                    />
                </NewTooltip>
            </>
        ),
        [disabled, meta.isEditable, meta.isViewable, toggleEditable, inCheckbox, toggleViewable]
    );

    return render;
};

enum Size {
    small = 'small',
    medium = 'medium',
    large = 'large',
}

const TemplateCheckbox = styled(Checkbox)<{ size?: keyof typeof Size | Size }>`
    --size: ${({ size }) => (size === Size.small ? '20px' : size === Size.medium ? '24px' : '32px')};
    --tick-size: calc(var(--size) / 2);

    width: min-content;

    display: inline-flex;
    align-items: center;
    margin-top: -6px;
    margin-left: 2rem !important;

    & + & {
        margin-left: 0;
    }

    .ant-checkbox {
        border-radius: 4px;
    }

    .ant-checkbox .ant-checkbox-checked:hover {
        border-radius: 4px;
    }

    :hover .ant-checkbox-inner::after {
        border: 2px solid ${colors.brand6};
        border-top: 0;
        border-left: 0;
    }

    :hover .ant-checkbox-disabled .ant-checkbox-inner::after {
        border: 2px solid #cecece;
        border-top: 0;
        border-left: 0;
    }
    :hover .ant-checkbox-checked .ant-checkbox-inner::after {
        border: 2px solid white;
        border-top: 0;
        border-left: 0;
    }

    .ant-checkbox-inner {
        height: var(--size);
        width: var(--size);
        position: relative;
        border-radius: 4px;
    }

    .ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner {
        background-color: rgba(0, 64, 228, 0.2);
        border-radius: 4px;
    }

    .ant-checkbox-checked .ant-checkbox-inner {
        background-color: ${colors.brand6};
        border-radius: 4px;
    }

    .ant-checkbox-inner::after {
        left: 50%;
        top: 45%;
        transform: translate(-50%, -50%) rotate(45deg) scale(1);
        height: var(--tick-size);
        width: calc(var(--tick-size) / 1.8);
        border: 2px solid #cecece;
        border-top: 0;
        border-left: 0;
        opacity: 1;
    }

    .ant-checkbox-checked .ant-checkbox-inner::after {
        border: 2px solid white;
        border-top: 0;
        border-left: 0;
    }

    .ant-checkbox-checked::after {
        border-radius: 4px;
    }
`;

type LockButtonProps = ButtonProps & {
    editable?: boolean;
    onToggle: () => void;
    disabled?: boolean;
    inCheckbox?: boolean;
};

const StyledLockButton = styled(Button)<{
    inCheckbox?: boolean;
}>`
    ${({ inCheckbox }) => `
        border-top-left-radius: ${inCheckbox ? '6' : '0'}px;
        border-bottom-left-radius: ${inCheckbox ? '6' : '0'}px;
        ${inCheckbox && 'margin-left: 2rem;'}
    `}
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    display: inline-flex;
    align-self: center;
    align-items: center;
    justify-content: center;
    color: ${colors.grey7} !important;
    max-width: 32px;

    :hover {
        border-left: 1px solid ${colors.brand5};
    }
`;

const LockButton = ({ editable, disabled, onToggle, ...rest }: LockButtonProps) => {
    return (
        <StyledLockButton disabled={disabled} onClick={() => onToggle()} {...rest}>
            <NewTooltip title={`User will ${!editable ? 'not ' : ''}be able to edit the default value`}>
                {!editable ? (
                    !disabled ? (
                        <LockFilled
                            style={{
                                color: colors.brand6,
                            }}
                        />
                    ) : (
                        <LockFilled />
                    )
                ) : (
                    <UnlockOutlined />
                )}
            </NewTooltip>
        </StyledLockButton>
    );
};

export default TemplateCheckbox;

export { TemplateCheckboxes };
